// Дорисовка недостающих дат перед записью ролика.
//
// Растр в ролике едет по датам, а тайлы есть не на каждую. Даты, чьи ДАННЫЕ уже
// в базе, стоят секунды на слой и глубину — их и дорисовываем. Даты без данных
// сюда не попадают вовсе: полный пайплайн (CMEMS плюс расчёт HSI, 44 минуты на
// дату) по клику «MP4» не запускается.
//
// См. docs/plans/2026-08-16-video-drift-timeline-design.md

import type { BBox } from '../api'

/** Опрос прогресса: раз в полторы секунды, как у кнопки «Загрузить». */
const POLL_MS = 1500
/** Потолок ожидания одной даты. Рендер слоя на одной глубине — секунды; минута
 *  означает, что дату взяла очередь чужой работы, и ждать её всей записью нельзя. */
const POLL_ATTEMPTS = 40

/** Что поставить в очередь по одной дате. */
export interface RenderRequest {
  date: string
  bbox?: BBox
  layers?: string[]
  depth?: number
  species_id?: number | null
  kind?: 'raw' | 'model' | 'upwelling'
}

export interface RenderDeps {
  prepare: (body: Record<string, unknown>) => Promise<{ task_id: string }>
  confirm: (taskId: string) => Promise<unknown>
  progress: (taskId: string) => Promise<{ status: string; progress_percent: number }>
  sleep?: (ms: number) => Promise<void>
  onProgress?: (share: number) => void
}

const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms))

/** Отрисовать даты ПО ОДНОЙ и вернуть те, что действительно готовы.
 *
 *  Строго последовательно: веер задач от кнопки «Загрузить» (42 пайплайна за
 *  клик) уже укладывал сервер в трэшинг памяти, и десять дат разом — та же
 *  история.
 *
 *  Провал одной даты не роняет запись: она просто не попадает в готовые, а окно
 *  ролика посчитается по факту. Молчать об этом нельзя — но говорит уже кнопка,
 *  называя записанный охват. */
export async function renderDates(
  items: RenderRequest[],
  deps: RenderDeps,
  attempts: number = POLL_ATTEMPTS,
): Promise<Set<string>> {
  const done = new Set<string>()
  const sleep = deps.sleep ?? wait
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    try {
      const { task_id } = await deps.prepare({
        bbox: item.bbox,
        date: item.date,
        layers: item.layers,
        depth: item.depth,
        species_id: item.species_id,
        kind: item.kind,
        // ТОЛЬКО показанный срез. Ролик идёт по суткам на ОДНОЙ глубине, а
        // рендер по умолчанию строит пирамиду из семнадцати уровней: прод
        // 16.08.2026 — 62 секунды на дату, из них полезен один уровень, а
        // остальные шестнадцать держат в однопоточной очереди следующую дату.
        // Пирамида нужна кнопке «Загрузить» (по ней ходят ползунком) — здесь нет.
        depth_only: true,
        // Суда — работа кнопки «Загрузить», а не записи ролика: без этого каждая
        // дата ставила бы ещё и задание добора позиций (см. runHsiRecompute).
        side_jobs: false,
      })
      await deps.confirm(task_id)
      for (let k = 0; k < attempts; k++) {
        const p = await deps.progress(task_id)
        if (p.status === 'completed') {
          done.add(item.date)
          break
        }
        if (p.status === 'failed' || p.status === 'data_unavailable') break
        await sleep(POLL_MS)
      }
    } catch {
      // Сеть моргнула — дата выпадает из ролика, но запись продолжается.
    }
    deps.onProgress?.((i + 1) / items.length)
  }
  return done
}
